import { isNullOrUndefined } from '@intelligence/v3/utils/common';

/**
 * 单次遍历获取数组中的最小值与最大值。
 *
 * @description
 * 该方法适用于已完成数值清洗的数组场景，避免调用方额外做空值分支判断。
 * 当 `nums` 不是数组或为空数组时，返回 `{ min: NaN, max: NaN }` 作为兜底占位值，
 * 便于调用方继续以解构方式读取结果。
 *
 * 注意：当前实现不会主动过滤 `NaN`、`Infinity` 等非有限数值；
 * 若输入中包含这类值，比较结果可能不符合预期，调用前应先完成过滤。
 *
 * @param nums 待计算的数值数组
 * @returns 包含最小值与最大值的对象；当入参无效或为空数组时返回 `NaN` 占位结果
 *
 * @example
 * getMinAndMax([3, 1, 8, 5]);
 * // { min: 1, max: 8 }
 *
 * @example
 * getMinAndMax([]);
 * // { min: NaN, max: NaN }
 */
export const getMinAndMax = (nums?: number[] | null): { min: number; max: number } => {
  if (!Array.isArray(nums) || nums.length === 0) {
    return { min: Number.NaN, max: Number.NaN };
  }

  let min = nums[0];
  let max = nums[0];

  for (let i = 1; i < nums.length; i += 1) {
    const value = nums[i];
    if (value < min) {
      min = value;
    }
    if (value > max) {
      max = value;
    }
  }

  return { min, max };
};

export const getValidNumber = (value?: number | string | null): number | null => {
  if (isNullOrUndefined(value)) {
    return null;
  }
  if (typeof value === 'string' && value.trim() === '') {
    return null;
  }

  const numberValue = Number(value);
  if (Number.isNaN(value)) {
    return null;
  }
  if (!Number.isFinite(numberValue)) {
    return null;
  }

  return numberValue;
};

/**
 * 默认数值格式化函数（K/M/B 格式）
 * @param value 数值
 * @param options 可选配置
 * @param options.decimalPlaces 小数位数，默认为2
 * @returns 格式化后的字符串
 */
export const formatKMB = (value?: number | string | null, options?: { decimalPlaces?: number }): string => {
  const { decimalPlaces = 2 } = options || {};
  const numValue = Number.parseFloat(value as string);

  if (Number.isNaN(numValue)) {
    return '';
  }

  const absValue = Math.abs(numValue);
  let formattedValue: string;

  if (absValue >= 1e9) {
    formattedValue = `${(numValue / 1e9).toFixed(decimalPlaces)}B`;
  } else if (absValue >= 1e6) {
    formattedValue = `${(numValue / 1e6).toFixed(decimalPlaces)}M`;
  } else if (absValue >= 1e3) {
    formattedValue = `${(numValue / 1e3).toFixed(decimalPlaces)}K`;
  } else {
    formattedValue = numValue.toFixed(decimalPlaces);
  }

  // 移除末尾的0（包括.0, .00等）
  formattedValue = formattedValue.replace(/(\.\d*?)0+([KMB]?)$/, '$1$2');
  formattedValue = formattedValue.replace(/\.([KMB]?)$/, '$1');

  return formattedValue;
};

export const formatKMBWithPlaceholder = (
  value?: number | string | null,
  options?: { decimalPlaces?: number; placeholder?: string },
): string => {
  const { placeholder = '-', ...restOptions } = options || {};
  return formatKMB(value, restOptions) || placeholder;
};

/**
 * 将小数格式化为百分比字符串
 * @date 2025-01-03
 * @description 将原始小数值乘以100并格式化为百分比,保留两位小数(不足补0),处理JS浮点数精度问题
 * @author lv.chen
 * @param {number | string | null | undefined} value - 原始数值(小数形式,如 0.1234 表示 12.34%)
 * @param {Object} options - 可选配置
 * @param {string} options.emptyValue - 无效值时返回的默认值,默认为空字符串
 * @returns {string} 格式化后的百分比字符串,如 "12.34%",无效值返回 emptyValue
 * @example
 * formatDecimalToPercentage(0.1234) // "12.34%"
 * formatDecimalToPercentage(0.1) // "10.00%"
 * formatDecimalToPercentage(0.12345) // "12.35%" (四舍五入)
 * formatDecimalToPercentage(null) // ""
 * formatDecimalToPercentage(NaN, { emptyValue: '-' }) // "-"
 */
export const formatDecimalToPercentage = (
  value: number | string | null | undefined,
  options?: { emptyValue?: string },
): string => {
  const { emptyValue = '' } = options || {};

  // 处理无效值
  if (value === null || value === undefined || value === '') {
    return emptyValue;
  }

  // 转换为数字
  const numValue = typeof value === 'string' ? Number.parseFloat(value) : value;

  // 检查是否为有效数字
  if (!Number.isFinite(numValue)) {
    return emptyValue;
  }

  // 乘以100并保留两位小数
  // 使用 toFixed 可以自动处理四舍五入和补0
  const percentageValue = (numValue * 100).toFixed(2);

  return `${percentageValue}%`;
};

/**
 * 格式化百分比数值
 * @param {number | string} value - 百分数值(如 0.1234 表示 12.34%)
 * @param {Object} options - 可选配置
 * @param {number} options.decimalPlaces - 小数位数,默认为 2
 * @returns {string} 格式化后的数值字符串
 * @example
 * formatPercentNum(0.5) // "+50%"
 * formatPercentNum(0.001) // "+0.10%"
 * formatPercentNum(0.001, { decimalPlaces: 3 }) // "+0.100%"
 * formatPercentNum(0.00001) // "< +0.01%"
 * formatPercentNum(0.00001, { decimalPlaces: 3 }) // "< +0.001%"
 * formatPercentNum('abc') // ""
 */
export const formatPercentNum = (value?: number | string | null, options?: { decimalPlaces?: number }): string => {
  const { decimalPlaces = 2 } = options || {};
  const numValue = Number.parseFloat(String(value));

  if (Number.isNaN(numValue)) {
    return '';
  }

  if (numValue === 0) {
    return '0';
  }

  const symbol = numValue < 0 ? '-' : '+';
  const result = numValue * 100;
  // Use decimalPlaces to preserve precision for threshold comparison
  const fixedResult = Number(result.toFixed(decimalPlaces));
  const absFixedResult = Math.abs(fixedResult);

  // Always return integer for >= 1
  if (absFixedResult >= 1) {
    return `${symbol}${Math.round(absFixedResult)}%`;
  }

  // Calculate dynamic threshold: 1 / 10^decimalPlaces
  const threshold = 1 / Math.pow(10, decimalPlaces);

  if (absFixedResult >= threshold) {
    // Remove trailing zeros by converting to Number and back to string
    const formattedValue = Number(absFixedResult.toFixed(decimalPlaces));
    return `${symbol}${formattedValue}%`;
  }

  // Generate dynamic threshold string (keep trailing zeros for threshold display)
  const thresholdStr = threshold.toFixed(decimalPlaces);
  if (numValue > 0) {
    return `< +${thresholdStr}%`;
  }

  return `< -${thresholdStr}%`;
};

export const formatPercentNumWithoutZero = (
  value?: number | string | null,
  options?: { decimalPlaces?: number },
  placeholder = '-',
): string => {
  const result = formatPercentNum(value, options || {});

  if (!result || result === '0') {
    return placeholder || '';
  }

  return result || placeholder || '';
};
